import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useWhatsapp } from './hooks/use-whatsapp';
import { WhatsappMessage } from './types/whatsapp';
import { useProfile } from "../../Components/Hooks/UserHooks";
import { ToastContainer } from "react-toastify";
import apiClient from '../../api/core/apiClient';

// Import components
import ContactList from './components/contactList/contactList';
import ChatHeader from './components/chatWindow/chatHeader';
import MessageList from './components/chatWindow/messageList';
import ChatInput from './components/chatWindow/chatInput';
import AssignAgentModal from './components/modals/assignAgentModal';
import AudioToast from "./components/audioToast";

// Reactstrap / Custom drawer components
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import CustomHeader from '../../Components/Common/CustomHeader';
import { getCustomerStateHistory } from '../customers/api/customers.api';
import CustomerStateTimeline from '../customers/components/CustomerStateTimeline';


// Scroll threshold: consider "at bottom" if within 120px
const SCROLL_BOTTOM_THRESHOLD = 120;

// Media cache helpers (shared with useMediaAttachment)
// LRU Cache implementation with size limit
class LRUCache<K, V> {
    private cache: Map<K, V>;
    private maxSize: number;

    constructor(maxSize: number = 50) {
        this.cache = new Map();
        this.maxSize = maxSize;
    }

    get(key: K): V | undefined {
        const value = this.cache.get(key);
        if (value !== undefined) {
            // Re-insert to mark as recently used
            this.cache.delete(key);
            this.cache.set(key, value);
        }
        return value;
    }

    set(key: K, value: V): void {
        if (this.cache.has(key)) {
            this.cache.delete(key);
        } else if (this.cache.size >= this.maxSize) {
            // Remove least recently used (first item)
            const firstKey = this.cache.keys().next().value;
            if (firstKey !== undefined) {
                const oldValue = this.cache.get(firstKey);
                if (oldValue && typeof oldValue === 'string') {
                    URL.revokeObjectURL(oldValue); // Revoke object URL to free memory
                }
                this.cache.delete(firstKey);
            }
        }
        this.cache.set(key, value);
    }

    has(key: K): boolean {
        return this.cache.has(key);
    }

    clear(): void {
        this.cache.forEach((value) => {
            if (typeof value === 'string') {
                URL.revokeObjectURL(value);
            }
        });
        this.cache.clear();
    }

    size(): number {
        return this.cache.size;
    }
}

const blobCache = new LRUCache<string, string>(50); // Max 50 blobs in memory
const activeRequests = new Map<string, Promise<Blob>>();

// Export cache for use in other components
export { blobCache, activeRequests };

// IndexedDB helpers for media persistence
const DB_NAME = 'whatsapp-media-cache';
const DB_VERSION = 2; // Increment version to add metadata
const STORE_NAME = 'blobs';

interface BlobMetadata {
    blob: Blob;
    timestamp: number;
}

function openMediaDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key: string): Promise<Blob | null> {
    try {
        const db = await openMediaDB();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(key);
            req.onsuccess = () => {
                const result = req.result;
                // Handle both old format (direct Blob) and new format (BlobMetadata)
                if (result) {
                    resolve((result as BlobMetadata).blob || (result as Blob));
                } else {
                    resolve(null);
                }
            };
            req.onerror = () => reject(req.error);
        });
    } catch {
        return null;
    }
}

async function idbSet(key: string, value: Blob): Promise<void> {
    try {
        const db = await openMediaDB();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const metadata: BlobMetadata = {
                blob: value,
                timestamp: Date.now()
            };
            const req = store.put(metadata, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch {
        // ignore persistence errors
    }
}

// Clean old entries from IndexedDB
async function cleanOldEntries(): Promise<void> {
    try {
        const db = await openMediaDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        const allKeys = await new Promise<string[]>((resolve, reject) => {
            const req = store.getAllKeys();
            req.onsuccess = () => resolve(req.result as string[]);
            req.onerror = () => reject(req.error);
        });

        const now = Date.now();
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        const maxEntries = 500;

        // Get all entries with their metadata to check timestamps
        const entries = await new Promise<Array<{ key: string; timestamp: number }>>((resolve, reject) => {
            const req = store.getAll();
            req.onsuccess = () => {
                const results = req.result as Array<{ key: string; blob: Blob; timestamp: number }>;
                const entries = allKeys.map((key, index) => ({
                    key,
                    timestamp: results[index]?.timestamp || 0
                }));
                resolve(entries);
            };
            req.onerror = () => reject(req.error);
        });

        // Sort by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);

        // Delete entries older than 7 days or if we have more than maxEntries
        let deletedCount = 0;
        for (const entry of entries) {
            if (entries.length - deletedCount > maxEntries || (now - entry.timestamp) > sevenDays) {
                store.delete(entry.key);
                deletedCount++;
            }
        }

        await new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });

        if (deletedCount > 0) {
            console.log(`Cleaned ${deletedCount} old entries from IndexedDB`);
        }
    } catch (error) {
        console.error('Error cleaning IndexedDB:', error);
    }
}

// Function to get media URL dynamically for the carousel
const getMediaUrl = async (mediaId: string, _type?: string): Promise<string> => {
    // Check memory cache first
    if (blobCache.has(mediaId)) {
        return blobCache.get(mediaId)!;
    }

    // Check IndexedDB cache
    const blob = await idbGet(mediaId);
    if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        blobCache.set(mediaId, objectUrl);
        return objectUrl;
    }

    // Download from API
    let request = activeRequests.get(mediaId);
    if (!request) {
        request = apiClient.get<Blob>(`/whatsapp/messages/media/${mediaId}`, {}, {
            responseType: 'blob'
        }) as Promise<Blob>;
        activeRequests.set(mediaId, request);
    }

    try {
        const downloadedBlob = await request;
        // Persist in IndexedDB
        await idbSet(mediaId, downloadedBlob);
        const objectUrl = URL.createObjectURL(downloadedBlob);
        blobCache.set(mediaId, objectUrl);
        activeRequests.delete(mediaId);

        // Clean old entries periodically (every 10th download)
        if (Math.random() < 0.1) {
            cleanOldEntries().catch(() => { });
        }

        return objectUrl;
    } catch (error) {
        console.error('Error fetching media:', error);
        activeRequests.delete(mediaId);
        throw error;
    }
};

const WhatsappModule = () => {
    // Clean old IndexedDB entries on mount
    useEffect(() => {
        cleanOldEntries().catch(() => { });
    }, []);

    const {
        contacts,
        contactsLoading,
        contactsHasMore,
        loadMoreContacts,
        loadContacts,
        unreadContacts,
        selectedContactId,
        setSelectedContactId,
        selectedContact,
        messages,
        messagesLoading,
        messagesHasMore,
        loadMoreMessages,
        loadTargetMessage,
        hasNewMessage,
        dismissNewMessage,
        sendMessage,
        resendMessage,
        audioStatus,
        showAudioUnlock,
        activateAudio,
        ignoreAudio,
        updateContactCustomerState,
        updateContactCustomerInfo,
        filterCurrentStateId,
        setFilterCurrentStateId,
    } = useWhatsapp();

    const [replyingTo, setReplyingTo] = React.useState<WhatsappMessage | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showScrollToTop, setShowScrollToTop] = React.useState(false);
    const [isChatOpenMobile, setIsChatOpenMobile] = useState(false);

    const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
    const [historyData, setHistoryData] = useState<{ data: any[]; nextCursor: string | null }>({ data: [], nextCursor: null });
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isFetchingMoreHistory, setIsFetchingMoreHistory] = useState(false);

    // Reset history states when contact changes
    useEffect(() => {
        setIsHistoryDrawerOpen(false);
        setHistoryData({ data: [], nextCursor: null });
    }, [selectedContactId]);

    // Fetch customer state history only when drawer is active
    useEffect(() => {
        const fetchHistory = async () => {
            if (!isHistoryDrawerOpen || !selectedContact?.customer?.id) return;
            setIsHistoryLoading(true);
            try {
                const history = await getCustomerStateHistory(selectedContact.customer.id, { limit: 10 });
                setHistoryData(history || { data: [], nextCursor: null });
            } catch (error) {
                console.error('Error fetching state history:', error);
                setHistoryData({ data: [], nextCursor: null });
            } finally {
                setIsHistoryLoading(false);
            }
        };

        fetchHistory();
    }, [isHistoryDrawerOpen, selectedContact?.customer?.id]);

    const loadMoreHistory = useCallback(async () => {
        if (isHistoryLoading || isFetchingMoreHistory || !historyData.nextCursor || !selectedContact?.customer?.id) return;
        setIsFetchingMoreHistory(true);
        try {
            const nextHistory = await getCustomerStateHistory(selectedContact.customer.id, {
                limit: 10,
                cursor: historyData.nextCursor,
            });
            if (nextHistory) {
                setHistoryData((prev) => ({
                    data: [...(prev?.data || []), ...(nextHistory.data || [])],
                    nextCursor: nextHistory.nextCursor,
                }));
            }
        } catch (error) {
            console.error('Error loading more state history:', error);
        } finally {
            setIsFetchingMoreHistory(false);
        }
    }, [isHistoryLoading, isFetchingMoreHistory, historyData.nextCursor, selectedContact?.customer?.id]);

    const handleHistoryScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 30;
        if (atBottom) {
            loadMoreHistory();
        }
    };

    useEffect(() => {
        if (!selectedContactId) {
            setIsChatOpenMobile(false);
        } else {
            setIsChatOpenMobile(true);
        }
    }, [selectedContactId]);

    const { isAdmin } = useProfile();

    // Manage Reply Focus
    useEffect(() => {
        if (replyingTo && inputRef.current) {
            inputRef.current.focus();
        }
    }, [replyingTo]);

    // Handle Escape key for Audio Toast
    useEffect(() => {
        if (!showAudioUnlock) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                ignoreAudio();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showAudioUnlock, ignoreAudio]);

    useEffect(() => {
        if (!selectedContactId) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setSelectedContactId(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedContactId, setSelectedContactId]);

    // Ref to the SimpleBar scroll container for chat messages
    const chatScrollRef = useRef<any>(null);

    // Scroll tracking refs
    const prevLastMessageId = useRef<string | undefined>();
    const prevContactId = useRef<string | null>(null);
    const prevScrollHeightRef = useRef<number>(0);
    const isFetchingOlderRef = useRef<boolean>(false);
    const isUserAtBottomRef = useRef<boolean>(true);


    // Track scroll position to know if user is at bottom
    useEffect(() => {
        if (!chatScrollRef.current) return;
        const el = chatScrollRef.current.getScrollElement?.() || chatScrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            const atBottom =
                el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD;
            isUserAtBottomRef.current = atBottom;

            // Dismiss "new message" indicator when user scrolls to bottom
            if (atBottom && hasNewMessage) {
                dismissNewMessage();
            }

            const notAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight >= SCROLL_BOTTOM_THRESHOLD;
            setShowScrollToTop(notAtBottom);
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [hasNewMessage, dismissNewMessage, selectedContactId, messagesLoading]);

    // Capture scrollHeight before older messages load
    useEffect(() => {
        if (!chatScrollRef.current) return;
        const el = chatScrollRef.current.getScrollElement?.() || chatScrollRef.current;
        if (!el) return;

        if (messagesLoading) {
            isFetchingOlderRef.current = true;
            prevScrollHeightRef.current = el.scrollHeight;
        }
    }, [messagesLoading]);

    // Smart scroll: preserve position on prepend, auto-scroll on new
    useEffect(() => {
        if (!chatScrollRef.current) return;
        const el = chatScrollRef.current.getScrollElement?.() || chatScrollRef.current;
        if (!el) return;

        // Restore scroll position after older messages were prepended
        if (isFetchingOlderRef.current && !messagesLoading) {
            const heightDiff = el.scrollHeight - prevScrollHeightRef.current;
            if (heightDiff > 0) {
                el.scrollTop = el.scrollTop + heightDiff;
            }
            isFetchingOlderRef.current = false;
        }

        const lastMessageId = messages[messages.length - 1]?.id;
        const isNewChat = prevContactId.current !== selectedContactId;
        const isNewMessageAtBottom = lastMessageId !== prevLastMessageId.current;

        if (isNewChat) {
            // New chat selected → always scroll to bottom
            el.scrollTop = el.scrollHeight;
            isUserAtBottomRef.current = true;
        } else if (isNewMessageAtBottom && isUserAtBottomRef.current) {
            // New message arrived and user was at bottom → scroll down
            el.scrollTop = el.scrollHeight;
        }
        // If user was scrolled up → don't force scroll (hasNewMessage flag handles it)

        prevLastMessageId.current = lastMessageId;
        prevContactId.current = selectedContactId;
    }, [messages, messagesLoading, selectedContactId]);


    // Handlers
    const handleSendMessage = useCallback((text: string, replyId?: string, file?: File) => {
        sendMessage(text, replyId, file);
    }, [sendMessage]);

    const handleInitiateReply = useCallback((message: WhatsappMessage) => {
        setReplyingTo(message);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    const jumpToMessage = useCallback(async (messageId: string) => {
        const highlightAndScroll = (el: HTMLElement) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('highlight-message');
            setTimeout(() => el.classList.remove('highlight-message'), 2000);
        };

        let targetEl = document.getElementById(`msg-${messageId}`);

        if (!targetEl) {
            const found = await loadTargetMessage(messageId);
            if (found) {
                setTimeout(() => {
                    const el = document.getElementById(`msg-${messageId}`);
                    if (el) highlightAndScroll(el);
                }, 150);
            }
        } else {
            highlightAndScroll(targetEl);
        }
    }, [loadTargetMessage]);

    const handleReplyClick = useCallback(async (message?: any) => {
        const replyMessageId = message?.replyMessageId || message?.replyMessage?.id;
        if (replyMessageId) await jumpToMessage(replyMessageId);
    }, [jumpToMessage]);

    const handleScrollToBottom = useCallback(() => {
        if (!chatScrollRef.current) return;
        const el = chatScrollRef.current.getScrollElement?.() || chatScrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        dismissNewMessage();
    }, [dismissNewMessage]);

    const handleScrollToBottomFab = useCallback(() => {
        if (!chatScrollRef.current) return;
        const el = chatScrollRef.current.getScrollElement?.() || chatScrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, []);

    const handleBackToContacts = useCallback(() => {
        setIsChatOpenMobile(false);
        setSelectedContactId(null);
    }, [setSelectedContactId]);

    const isChatClosed = messages.length > 0
        ? Boolean(messages[messages.length - 1]?.conversationClosed)
        : Boolean(selectedContact?.lastMessage?.conversationClosed);
    const isInputDisabled = isChatClosed || !!selectedContact?.botActive;

    const activeAgentName = selectedContact?.conversations?.find(
        (c: { closedAt?: string | null; agent?: { name: string } }) => !c.closedAt
    )?.agent?.name;



    return (
        <>
            <div className="chat-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1 position-relative">
                {/* Left sidebar: Contacts */}
                <div className={`chat-leftsidebar px-2 border-end border-light d-flex flex-column ${isChatOpenMobile ? 'd-none d-lg-flex' : ''}`}>
                    <ContactList
                        contacts={contacts}
                        contactsLoading={contactsLoading}
                        contactsHasMore={contactsHasMore}
                        loadMoreContacts={loadMoreContacts}
                        selectedContactId={selectedContactId}
                        setSelectedContactId={setSelectedContactId}
                        unreadContacts={unreadContacts}
                        audioStatus={audioStatus}
                        activateAudio={activateAudio}
                        ignoreAudio={ignoreAudio}
                        filterStateId={filterCurrentStateId}
                        setFilterStateId={setFilterCurrentStateId}
                    />
                </div>

                {/* Right panel: Conversation */}
                <div className={`user-chat w-100 overflow-hidden d-flex flex-column ${isChatOpenMobile ? 'user-chat-show' : ''}`}>
                    {/* Empty state */}
                    {!selectedContactId ? (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4"
                            style={{ background: 'var(--vz-secondary-bg)' }}>
                            <i className="ri-message-3-line"
                                style={{ fontSize: '5rem', color: '#d19221', opacity: 0.35 }}></i>
                            <h5 className="mt-3 text-muted fw-normal">Ningún chat seleccionado</h5>
                            <p className="text-muted fs-13">Seleccione un contacto para ver los mensajes</p>
                        </div>
                    ) : (
                        <>
                            {/* Chat header */}
                            <ChatHeader
                                selectedContact={selectedContact}
                                selectedContactId={selectedContactId}
                                isAdmin={isAdmin}
                                activeAgentName={activeAgentName}
                                isChatClosed={isChatClosed}
                                setIsModalOpen={setIsModalOpen}
                                onJumpToMessage={jumpToMessage}
                                onBack={handleBackToContacts}
                                onUpdateCustomerState={updateContactCustomerState}
                                onUpdateCustomerInfo={updateContactCustomerInfo}
                                onOpenStateHistory={() => setIsHistoryDrawerOpen(true)}
                            />

                            {/* Messages body */}
                            <MessageList
                                messages={messages}
                                messagesLoading={messagesLoading}
                                messagesHasMore={messagesHasMore}
                                loadMoreMessages={loadMoreMessages}
                                chatScrollRef={chatScrollRef}
                                hasNewMessage={hasNewMessage}
                                handleScrollToBottom={handleScrollToBottom}
                                showScrollToTop={showScrollToTop}
                                handleScrollToBottomFab={handleScrollToBottomFab}
                                isInputDisabled={isInputDisabled}
                                selectedContact={selectedContact}
                                handleInitiateReply={handleInitiateReply}
                                handleReplyClick={handleReplyClick}
                                resendMessage={resendMessage}
                                getMediaUrl={getMediaUrl}
                            />

                            {/* Message input */}
                            <ChatInput
                                replyingTo={replyingTo}
                                setReplyingTo={setReplyingTo}
                                isInputDisabled={isInputDisabled}
                                selectedContact={selectedContact}
                                inputRef={inputRef}
                                onSendMessage={handleSendMessage}
                            />
                        </>
                    )}
                </div>
                {/* Audio Unlock Toast */}
                <AudioToast
                    showAudioUnlock={showAudioUnlock}
                    activateAudio={activateAudio}
                    ignoreAudio={ignoreAudio}
                />
            </div>

            {/* Assign Agent Modal */}
            <AssignAgentModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                selectedContact={selectedContact}
                selectedContactId={selectedContactId}
                loadContacts={loadContacts}
            />

            {/* Customer State History Drawer */}
            <Offcanvas
                isOpen={isHistoryDrawerOpen}
                toggle={() => setIsHistoryDrawerOpen(false)}
                direction="end"
                backdrop={false}
                scrollable={true}
                style={{ width: '380px', borderLeft: '1px solid var(--vz-border-color)' }}
            >
                <CustomHeader
                    title="Historial de Estados"
                    onClose={() => setIsHistoryDrawerOpen(false)}
                    component={OffcanvasHeader}
                />
                <OffcanvasBody className="flex-grow-1 overflow-auto" onScroll={handleHistoryScroll}>
                    <CustomerStateTimeline
                        historyData={historyData}
                        isLoading={isHistoryLoading}
                        isFetchingMore={isFetchingMoreHistory}
                    />
                </OffcanvasBody>
            </Offcanvas>

            <ToastContainer closeButton={false} limit={1} />
        </>
    );
};

export default WhatsappModule;
